import React, { useEffect, useState } from "react";
import api from "../api";
import AdminUnlock from "../dashboard/AdminUnlock";
import {
  LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer,
  Tooltip, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";
import {
  Bot, Zap, Users, ShieldCheck, Ban, CheckCircle2, Upload, RefreshCw, Search,
  TrendingUp, Wallet, Gift, Lock, Flame, GitBranch, Pencil, X, Mail, Send,
} from "lucide-react";

/* ═══ STEP 6 — ADMIN CONTROL PANEL (/admin) ═══
   Requires admin JWT + ADMIN_ACCESS_TOKEN (server-side lock from Step 2). */
export default function AdminPanelStep6() {
  return (
    <div style={{ minHeight: "100vh", background: "#050914", color: "#E7ECFF", padding: "32px 20px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ margin: 0, fontSize: 32, color: "#FFD700", display: "flex", alignItems: "center", gap: 12 }}>
            <ShieldCheck size={30} /> Admin Control Panel
          </h1>
          <div style={{ color: "#8B94B8", fontSize: 13, marginTop: 4 }}>Turbo Global Coin — Web3 Fiat Simulation Backoffice</div>
        </div>
        <AdminUnlock>
          <PanelContent />
        </AdminUnlock>
      </div>
    </div>
  );
}

function PanelContent() {
  return (
    <>
      <MasterLedger />
      <MlmMatrixVisualizer />
      <GrowthCharts />
      <DeflationVisualizer />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
        <AiRecruiterSwitch />
        <GenesisWhitelist />
      </div>
      <NodeTreeViewer />
      <UserDirectory />
    </>
  );
}

/* ══════════════ STEP 23 · 8-Level MLM Structural Matrix (admin overview) ══════════════ */
function MlmMatrixVisualizer() {
  const rows = [
    { lvl: 1, pct: 20, hue: "#FFD700" },
    { lvl: 2, pct: 10, hue: "#FFC300" },
    { lvl: 3, pct: 5,  hue: "#FFAA00" },
    { lvl: 4, pct: 3,  hue: "#FF8C42" },
    { lvl: 5, pct: 2,  hue: "#FF6B35" },
    { lvl: 6, pct: 2,  hue: "#E85D75" },
    { lvl: 7, pct: 1,  hue: "#BC4B90" },
    { lvl: 8, pct: 1,  hue: "#8B3A9E" },
  ];
  return (
    <section data-testid="admin-mlm-matrix"
      style={{ marginBottom: 20, padding: 20, borderRadius: 14,
               background: "linear-gradient(135deg, rgba(255,215,0,0.05), rgba(188,19,254,0.04))",
               border: "1px solid rgba(255,215,0,0.30)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <GitBranch size={18} color="#FFD700" />
        <h2 style={{ margin: 0, fontSize: 18, color: "#FFD700" }}>
          8-уровневая структурная маркетинговая матрица
        </h2>
      </div>
      <div style={{ fontSize: 12, color: "#8B94B8", marginBottom: 14 }}>
        Реальное распределение 44 % с каждой оплаты активации ($10 / $50 / $250) — расчёт мгновенный через смарт-контракт на Polygon Chain 137.
      </div>
      <div data-testid="admin-mlm-matrix-grid"
        style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 8 }}>
        {rows.map((r) => (
          <div key={r.lvl} data-testid={`admin-mlm-level-${r.lvl}`}
            style={{ padding: 12, borderRadius: 10, textAlign: "center",
                     background: `linear-gradient(135deg, ${r.hue}18, rgba(255,255,255,0.02))`,
                     border: `1px solid ${r.hue}66` }}>
            <div style={{ fontSize: 10, letterSpacing: 1.4, textTransform: "uppercase",
                          color: r.hue, fontWeight: 800, marginBottom: 4 }}>
              L{r.lvl}
            </div>
            <div style={{ fontSize: 24, fontWeight: 900, color: r.hue, lineHeight: 1 }}>
              {r.pct}%
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 12, padding: 10, borderRadius: 8,
                    background: "rgba(0,242,255,0.06)",
                    border: "1px solid rgba(0,242,255,0.30)",
                    fontSize: 11, color: "#00F2FF", textAlign: "center", fontWeight: 700 }}>
        Итого 44 % → 8 uplines · Остальные 56 % → project fund + Turbo-Bank ликвидность
      </div>
    </section>
  );
}

/* ══════════════════ 1. MASTER LEDGER ══════════════════ */
function MasterLedger() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin-panel/ledger"); setData(r.data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, []);

  return (
    <section data-testid="admin-master-ledger" style={sectionStyle}>
      <div style={sectionHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <TrendingUp size={20} color="#10F4A7" />
          <h2 style={{ margin: 0, fontSize: 18, color: "#10F4A7" }}>Master Ledger — Global Analytics</h2>
        </div>
        <button onClick={load} disabled={loading} style={btnGhost} data-testid="ledger-refresh"><RefreshCw size={13} className={loading ? "spin" : ""} /></button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        <StatCell testId="ledger-project-56" label="56% Project Fund" value={fmtEur(data?.payments.project_56_total)} color="#FFD700" icon={<Wallet size={16} />} />
        <StatCell testId="ledger-multilevel-44" label="44% Multilevel (8 tiers)" value={fmtEur(data?.payments.multilevel_44_total)} color="#00F2FF" icon={<Zap size={16} />} />
        <StatCell testId="ledger-total-users" label="Total Users" value={fmtNum(data?.users.total)} color="#BC13FE" icon={<Users size={16} />} />
        <StatCell testId="ledger-payments-count" label="Total Payments" value={fmtNum(data?.payments.total_payments)} color="#10F4A7" icon={<CheckCircle2 size={16} />} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 8, marginTop: 12 }}>
        <MiniStat testId="ledger-l1" label="L1 · 20%" value={fmtEur(data?.payments.level_1_20_total)} color="#10F4A7" />
        <MiniStat testId="ledger-l2" label="L2 · 10%" value={fmtEur(data?.payments.level_2_10_total)} color="#00F2FF" />
        <MiniStat testId="ledger-l3" label="L3 · 5%" value={fmtEur(data?.payments.level_3_5_total)} color="#BC13FE" />
        <MiniStat testId="ledger-l4" label="L4 · 3%" value={fmtEur(data?.payments.level_4_3_total)} color="#FF9F43" />
        <MiniStat testId="ledger-l5" label="L5 · 2%" value={fmtEur(data?.payments.level_5_2_total)} color="#FF6B9D" />
        <MiniStat testId="ledger-l6" label="L6 · 2%" value={fmtEur(data?.payments.level_6_2_total)} color="#5EEAD4" />
        <MiniStat testId="ledger-l7" label="L7 · 1%" value={fmtEur(data?.payments.level_7_1_total)} color="#A78BFA" />
        <MiniStat testId="ledger-l8" label="L8 · 1%" value={fmtEur(data?.payments.level_8_1_total)} color="#FDA4AF" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginTop: 12 }}>
        <MiniStat testId="ledger-active" label="Active" value={fmtNum(data?.users.active)} color="#10F4A7" />
        <MiniStat testId="ledger-genesis" label="Genesis" value={fmtNum(data?.users.genesis)} color="#FFD700" />
        <MiniStat testId="ledger-blocked" label="Blocked" value={fmtNum(data?.users.blocked)} color="#FF3D6C" />
        <MiniStat testId="ledger-airdrops" label={`Airdrops (${data?.airdrops.locked ?? "—"} locked)`} value={fmtNum(data?.airdrops.total)} color="#00F2FF" />
      </div>
    </section>
  );
}

/* ══════════════════ 2. AI RECRUITER MASTER SWITCH ══════════════════ */
function AiRecruiterSwitch() {
  const [enabled, setEnabled] = useState(null);
  const [busy, setBusy] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const load = async () => {
    try { const r = await api.get("/admin-panel/ai-recruiter"); setEnabled(!!r.data.enabled); setUpdatedAt(r.data.updatedAt); }
    catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);
  const toggle = async () => {
    if (enabled === null) return;
    if (!window.confirm(`Действительно ${enabled ? "ОТКЛЮЧИТЬ" : "ВКЛЮЧИТЬ"} AI Recruiter для ВСЕЙ сети?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin-panel/ai-recruiter", { enabled: !enabled });
      setEnabled(!!r.data.enabled);
      await load();
    } catch (e) { alert(e.response?.data?.detail || "Ошибка"); }
    finally { setBusy(false); }
  };
  return (
    <section data-testid="admin-ai-master" style={sectionStyle}>
      <div style={sectionHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Bot size={20} color="#BC13FE" />
          <h2 style={{ margin: 0, fontSize: 18, color: "#BC13FE" }}>Разрешить запуск всей сети · AI-Агент</h2>
        </div>
      </div>
      <div style={{ padding: 20, background: "rgba(0,0,0,0.3)", borderRadius: 12, textAlign: "center" }}>
        <div style={{ fontSize: 12, color: "#8B94B8", marginBottom: 12 }}>
          Единая кнопка запускает / останавливает все фоновые cron-задачи AI-Агента по всей сети.
        </div>
        <button onClick={toggle} disabled={busy || enabled === null}
          data-testid="admin-ai-toggle"
          style={{
            width: 260, height: 60, borderRadius: 30,
            background: enabled === null ? "#333"
              : enabled ? "linear-gradient(135deg, #10F4A7, #00F2FF)"
              : "rgba(255,255,255,0.06)",
            border: enabled ? "none" : "1px solid #FF3D6C",
            color: enabled ? "#050914" : "#FF3D6C", fontWeight: 800, fontSize: 14,
            cursor: busy ? "wait" : "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
          {enabled === null ? "…" : busy ? "…" :
            enabled ? <><Ban size={16} /> Остановить ИИ-Агента</> :
              <><CheckCircle2 size={18} /> Разрешить запуск всей сети</>}
        </button>
        <div style={{ fontSize: 11, color: "#5F6884", marginTop: 12 }}>
          {enabled === true && "AI-Агент активен для всей сети"}
          {enabled === false && "AI-Агент остановлен"}
          {updatedAt && <div>Изменено: {new Date(updatedAt).toLocaleString("ru-RU")}</div>}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════ 3. GENESIS WHITELIST ══════════════════ */
function GenesisWhitelist() {
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [manualIds, setManualIds] = useState("");
  const [manualBusy, setManualBusy] = useState(false);
  const [manualResult, setManualResult] = useState(null);

  const uploadCsv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCsvBusy(true); setCsvResult(null);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/admin-panel/genesis/upload-csv", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setCsvResult(r.data);
    } catch (e2) { alert(e2.response?.data?.detail || "Ошибка загрузки"); }
    finally { setCsvBusy(false); e.target.value = ""; }
  };

  const manualMark = async () => {
    const lines = manualIds.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!lines.length) return;
    setManualBusy(true); setManualResult(null);
    try {
      const wallets = lines.filter((l) => l.startsWith("0x") || l.length > 20 && !l.includes("@"));
      const emails = lines.filter((l) => l.includes("@"));
      const user_ids = lines.filter((l) => !wallets.includes(l) && !emails.includes(l));
      const r = await api.post("/admin-panel/genesis/mark",
        { wallet_addresses: wallets, emails, user_ids, genesis: true });
      setManualResult(r.data);
    } catch (e) { alert(e.response?.data?.detail || "Ошибка"); }
    finally { setManualBusy(false); }
  };

  const downloadTemplate = () => {
    const csv = "wallet_address,email,telegram_username\n0xabc123...,alice@example.com,@alice\n,bob@example.com,\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "genesis-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section data-testid="admin-genesis" style={sectionStyle}>
      <div style={sectionHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Gift size={20} color="#FFD700" />
          <h2 style={{ margin: 0, fontSize: 18, color: "#FFD700" }}>Genesis Whitelist</h2>
        </div>
        <button onClick={downloadTemplate} style={btnGhost} data-testid="genesis-download-template">Template</button>
      </div>
      <div style={{ padding: 14, background: "rgba(255,215,0,0.05)", border: "1px solid rgba(255,215,0,0.25)", borderRadius: 10, marginBottom: 12, fontSize: 12, color: "#C9D1E8" }}>
        Отмеченные пользователи получают <b>is_genesis_provider=true</b> + <b>is_agent_active=true</b>, обходят €10 fee и получают LOCKED airdrop entry.
      </div>

      {/* CSV upload */}
      <label style={{ display: "block", marginBottom: 10 }}>
        <input type="file" accept=".csv" onChange={uploadCsv} disabled={csvBusy}
          data-testid="genesis-csv-upload"
          style={{ display: "none" }} />
        <div style={{ ...btnPrimary, background: "linear-gradient(135deg,#FFD700,#FF9F43)",
          textAlign: "center", cursor: csvBusy ? "wait" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <Upload size={16} /> {csvBusy ? "Загрузка…" : "Массовая загрузка CSV"}
        </div>
      </label>

      {csvResult && (
        <div data-testid="genesis-csv-result"
          style={{ padding: 10, background: "rgba(16,244,167,0.08)", border: "1px solid #10F4A7", borderRadius: 8, fontSize: 12, marginBottom: 12 }}>
          ✓ Создано: {csvResult.created} · Отмечено: {csvResult.flagged} · Пропущено: {csvResult.skipped}
        </div>
      )}

      {/* Manual list */}
      <div style={{ fontSize: 11, color: "#8B94B8", marginBottom: 4, marginTop: 8 }}>Или список (wallet-адреса / emails / user_ids через запятую):</div>
      <textarea value={manualIds} onChange={(e) => setManualIds(e.target.value)}
        placeholder="0xabc123..., bob@example.com, uuid-here"
        data-testid="genesis-manual-input"
        rows={3}
        style={{ width: "100%", padding: 10, borderRadius: 8, background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.1)", color: "#E7ECFF", fontSize: 12, fontFamily: "monospace", resize: "vertical" }} />
      <button onClick={manualMark} disabled={manualBusy || !manualIds.trim()}
        data-testid="genesis-manual-mark"
        style={{ ...btnPrimary, width: "100%", marginTop: 8, background: "#FFD700", color: "#050914" }}>
        {manualBusy ? "Обработка…" : "Отметить как Genesis"}
      </button>

      {manualResult && (
        <div data-testid="genesis-manual-result"
          style={{ padding: 10, background: "rgba(16,244,167,0.08)", border: "1px solid #10F4A7", borderRadius: 8, fontSize: 12, marginTop: 10 }}>
          ✓ Найдено {manualResult.matched}, обновлено {manualResult.updated}, airdrops создано {manualResult.airdrops_created}
        </div>
      )}
    </section>
  );
}

/* ══════════════════ 4. USER DIRECTORY ══════════════════ */
function UserDirectory() {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ q, limit: "50" });
      if (filter === "genesis") params.set("genesis_only", "true");
      if (filter === "active") params.set("active_only", "true");
      if (filter === "blocked") params.set("blocked_only", "true");
      const r = await api.get(`/admin-panel/users?${params}`);
      setUsers(r.data.users || []); setTotal(r.data.total || 0);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const activate = async (uid) => {
    if (!window.confirm("Активировать пользователя?")) return;
    try { await api.post(`/admin-panel/users/${uid}/activate`); load(); }
    catch (e) { alert(e.response?.data?.detail || "Ошибка"); }
  };
  const block = async (uid) => {
    if (!window.confirm("Заблокировать пользователя? is_agent_active станет false.")) return;
    try { await api.post(`/admin-panel/users/${uid}/block`); load(); }
    catch (e) { alert(e.response?.data?.detail || "Ошибка"); }
  };

  // STEP 22 — Manual email / telegram_username override
  const [editing, setEditing] = useState(null);  // {user_id, email, telegram_username}
  const [editForm, setEditForm] = useState({ email: "", telegram_username: "" });
  const [editBusy, setEditBusy] = useState(false);
  const [editErr, setEditErr] = useState(null);
  const openEditor = (u) => {
    setEditing(u);
    setEditForm({ email: u.email || "", telegram_username: u.telegram_username || "" });
    setEditErr(null);
  };
  const saveEditor = async () => {
    setEditBusy(true); setEditErr(null);
    try {
      const payload = {};
      if (editForm.email !== (editing.email || "")) payload.email = editForm.email;
      if (editForm.telegram_username !== (editing.telegram_username || "")) payload.telegram_username = editForm.telegram_username;
      if (Object.keys(payload).length === 0) { setEditErr("Ничего не изменилось"); setEditBusy(false); return; }
      await api.post(`/admin-panel/users/${editing.user_id}/edit-profile`, payload);
      setEditing(null); load();
    } catch (e) {
      setEditErr(e?.response?.data?.detail || "Ошибка сохранения");
    } finally { setEditBusy(false); }
  };

  return (
    <section data-testid="admin-user-directory" style={sectionStyle}>
      <div style={sectionHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Users size={20} color="#00F2FF" />
          <h2 style={{ margin: 0, fontSize: 18, color: "#00F2FF" }}>User Directory — {total} users</h2>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
          <Search size={14} style={{ position: "absolute", left: 12, top: 12, color: "#5F6884" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Поиск: wallet / email / user_id / telegram"
            data-testid="admin-user-search"
            style={{ width: "100%", padding: "10px 12px 10px 34px", borderRadius: 8, background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.1)", color: "#E7ECFF", fontSize: 13, outline: "none" }} />
        </div>
        <button onClick={load} style={{ ...btnPrimary, background: "#00F2FF", color: "#050914" }} data-testid="admin-user-search-btn">Найти</button>
        <select value={filter} onChange={(e) => setFilter(e.target.value)}
          data-testid="admin-user-filter"
          style={{ padding: "10px 12px", borderRadius: 8, background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.1)", color: "#E7ECFF", fontSize: 13 }}>
          <option value="all">Все</option>
          <option value="active">Только Active</option>
          <option value="genesis">Только Genesis</option>
          <option value="blocked">Только Blocked</option>
        </select>
      </div>

      <div style={{ background: "rgba(0,0,0,0.3)", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "grid", gridTemplateColumns: "1.4fr 1.6fr 0.8fr 0.7fr 0.7fr 1.2fr", gap: 10, fontSize: 11, color: "#8B94B8", textTransform: "uppercase", letterSpacing: 1 }}>
          <div>Wallet</div><div>Email / TG</div><div>Country</div><div>Status</div><div>Genesis</div><div>Действия</div>
        </div>
        {loading && <div style={{ padding: 24, textAlign: "center", color: "#8B94B8" }}>Загрузка…</div>}
        {!loading && users.length === 0 && <div style={{ padding: 24, textAlign: "center", color: "#5F6884" }}>Нет пользователей</div>}
        {!loading && users.map((u) => (
          <div key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}
            style={{ padding: "10px 14px", borderBottom: "1px dashed rgba(255,255,255,0.05)", display: "grid", gridTemplateColumns: "1.4fr 1.6fr 0.8fr 0.7fr 0.7fr 1.2fr", gap: 10, fontSize: 12, alignItems: "center" }}>
            <code style={{ color: "#00F2FF", fontSize: 11 }}>{u.wallet_address?.slice(0, 14)}…</code>
            <div>
              <div>{u.email || <span style={{ color: "#5F6884" }}>—</span>}</div>
              {u.telegram_username && <div style={{ fontSize: 10, color: "#8B94B8" }}>@{u.telegram_username}</div>}
            </div>
            <div style={{ color: "#8B94B8" }}>—</div>
            <div>
              {u.is_blocked ? <span style={{ color: "#FF3D6C", fontWeight: 700 }}>BLOCKED</span>
                : u.is_agent_active ? <span style={{ color: "#10F4A7", fontWeight: 700 }}>ACTIVE</span>
                : <span style={{ color: "#8B94B8" }}>Pending</span>}
            </div>
            <div>{u.is_genesis_provider ? <span style={{ color: "#FFD700", fontWeight: 700 }}>★ Genesis</span> : <span style={{ color: "#5F6884" }}>—</span>}</div>
            <div style={{ display: "flex", gap: 4 }}>
              <button onClick={() => activate(u.user_id)}
                data-testid={`admin-activate-${u.user_id}`}
                title="Activate"
                style={{ padding: "5px 8px", borderRadius: 6, background: "rgba(16,244,167,0.1)", border: "1px solid #10F4A7", color: "#10F4A7", cursor: "pointer", fontSize: 11 }}>
                <CheckCircle2 size={12} />
              </button>
              <button onClick={() => block(u.user_id)}
                data-testid={`admin-block-${u.user_id}`}
                title="Остановить ИИ-Агента для этого провайдера"
                style={{ padding: "5px 8px", borderRadius: 6, background: "rgba(255,61,108,0.1)", border: "1px solid #FF3D6C", color: "#FF3D6C", cursor: "pointer", fontSize: 11 }}>
                <Ban size={12} />
              </button>
              <button onClick={() => openEditor(u)}
                data-testid={`admin-edit-profile-${u.user_id}`}
                title="Редактировать профиль (email / telegram)"
                style={{ padding: "5px 8px", borderRadius: 6, background: "rgba(255,215,0,0.10)", border: "1px solid #FFD700", color: "#FFD700", cursor: "pointer", fontSize: 11 }}>
                <Pencil size={12} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* STEP 22 — Admin Edit-Profile Modal */}
      {editing && (
        <div data-testid="admin-edit-profile-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setEditing(null); }}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
                   display: "flex", alignItems: "center", justifyContent: "center",
                   zIndex: 9999 }}>
          <div style={{ maxWidth: 460, width: "92%", background: "#0B1220",
                        border: "1px solid rgba(255,215,0,0.35)", borderRadius: 14,
                        padding: 22, boxShadow: "0 20px 60px rgba(255,215,0,0.15)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Pencil size={18} color="#FFD700" />
                <h3 style={{ margin: 0, color: "#FFD700", fontSize: 16 }}>Редактировать профиль</h3>
              </div>
              <button onClick={() => setEditing(null)}
                data-testid="admin-edit-profile-close"
                style={{ background: "transparent", border: "none", color: "#8B94B8", cursor: "pointer" }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ fontSize: 11, color: "#8B94B8", marginBottom: 14 }}>
              user_id · <code style={{ color: "#00F2FF" }}>{editing.user_id}</code>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11,
                              color: "#8B94B8", textTransform: "uppercase", letterSpacing: 1,
                              marginBottom: 6 }}>
                <Mail size={11} /> Email
              </label>
              <input value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                data-testid="admin-edit-profile-email"
                placeholder="new-email@example.com"
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8,
                         background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.10)",
                         color: "#E7ECFF", fontSize: 13, outline: "none" }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11,
                              color: "#8B94B8", textTransform: "uppercase", letterSpacing: 1,
                              marginBottom: 6 }}>
                <Send size={11} /> Telegram username
              </label>
              <input value={editForm.telegram_username}
                onChange={(e) => setEditForm({ ...editForm, telegram_username: e.target.value })}
                data-testid="admin-edit-profile-telegram"
                placeholder="@new_handle (без @)"
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8,
                         background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.10)",
                         color: "#E7ECFF", fontSize: 13, outline: "none" }} />
            </div>
            {editErr && (
              <div data-testid="admin-edit-profile-error"
                style={{ padding: 10, borderRadius: 8, background: "rgba(255,61,108,0.1)",
                         border: "1px solid #FF3D6C", color: "#FF3D6C", fontSize: 12, marginBottom: 12 }}>
                {editErr}
              </div>
            )}
            <div style={{ padding: 10, borderRadius: 8, background: "rgba(255,215,0,0.08)",
                          border: "1px solid rgba(255,215,0,0.30)", fontSize: 11,
                          color: "#FFD700", marginBottom: 14 }}>
              Правки применяются к записям <b>users_fiat_simulation</b> и <b>tgc_users</b> одновременно. Используйте только для восстановления доступа к учётной записи.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setEditing(null)}
                data-testid="admin-edit-profile-cancel"
                style={{ padding: "9px 16px", borderRadius: 8, background: "transparent",
                         border: "1px solid rgba(255,255,255,0.15)", color: "#8B94B8",
                         cursor: "pointer", fontSize: 13 }}>
                Отмена
              </button>
              <button onClick={saveEditor} disabled={editBusy}
                data-testid="admin-edit-profile-save"
                style={{ padding: "9px 18px", borderRadius: 8, background: "#FFD700",
                         color: "#050914", border: "none", cursor: editBusy ? "wait" : "pointer",
                         fontSize: 13, fontWeight: 800 }}>
                {editBusy ? "Сохранение…" : "Сохранить"}
              </button>
            </div>
          </div>
        </div>
      )}
      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </section>
  );
}

/* ── shared UI helpers ── */
const sectionStyle = { padding: 20, background: "#0B1220", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, marginBottom: 20 };
const sectionHeader = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 };
const btnPrimary = { padding: "10px 16px", borderRadius: 8, fontSize: 13, fontWeight: 700, background: "#00F2FF", color: "#050914", border: "none", cursor: "pointer" };
const btnGhost = { padding: "6px 10px", borderRadius: 6, fontSize: 11, background: "transparent", color: "#8B94B8", border: "1px solid rgba(255,255,255,0.1)", cursor: "pointer" };
const fmtEur = (n) => n === null || n === undefined ? "—" : "€" + Number(n).toLocaleString("ru-RU", { maximumFractionDigits: 2 });
const fmtNum = (n) => n === null || n === undefined ? "—" : Number(n).toLocaleString("ru-RU");

function StatCell({ label, value, color, icon, testId }) {
  return (
    <div data-testid={testId} style={{ padding: 14, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#8B94B8", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
        <span style={{ color }}>{icon}</span>{label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color, fontFeatureSettings: '"tnum"' }}>{value}</div>
    </div>
  );
}
function MiniStat({ label, value, color, testId }) {
  return (
    <div data-testid={testId} style={{ padding: 10, background: "rgba(255,255,255,0.02)", borderRadius: 8, textAlign: "center" }}>
      <div style={{ fontSize: 10, color: "#8B94B8", marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

/* ══════════════════ 5. GROWTH CHARTS (Step 9) ══════════════════ */
function GrowthCharts() {
  const [series, setSeries] = useState(null);
  const [pie, setPie] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => {
    api.get(`/admin-panel/growth-timeseries?days=${days}`).then(r => setSeries(r.data)).catch(() => {});
    api.get("/admin-panel/finance-pie").then(r => setPie(r.data)).catch(() => {});
  }, [days]);
  return (
    <section data-testid="admin-growth-charts" style={sectionStyle}>
      <div style={sectionHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <TrendingUp size={20} color="#10F4A7" />
          <h2 style={{ margin: 0, fontSize: 18, color: "#10F4A7" }}>Growth Charts</h2>
        </div>
        <select value={days} onChange={(e) => setDays(+e.target.value)} data-testid="growth-days-select"
          style={{ padding: "4px 8px", background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.1)", color: "#E7ECFF", borderRadius: 6, fontSize: 12 }}>
          <option value={7}>7d</option><option value={30}>30d</option><option value={90}>90d</option>
        </select>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        {/* Chart 1: Cumulative merchant nodes → 9M target */}
        <div data-testid="chart-merchant-growth" style={{ background: "rgba(0,0,0,0.3)", padding: 14, borderRadius: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 12 }}>
            <span style={{ color: "#8B94B8" }}>Merchant Nodes → 9,000,000 target</span>
            <span style={{ color: "#10F4A7", fontWeight: 700 }}>
              {series ? `${fmtNum(series.current)} / ${fmtNum(series.target)} (${series.progress_pct?.toFixed(4)}%)` : "—"}
            </span>
          </div>
          {series ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={series.series}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" stroke="#5F6884" tick={{ fontSize: 10 }} />
                <YAxis stroke="#5F6884" tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "#0B1220", border: "1px solid #10F4A7", fontSize: 12 }} />
                <Line type="monotone" dataKey="cumulative" stroke="#10F4A7" strokeWidth={2} dot={false} name="Cumulative" />
              </LineChart>
            </ResponsiveContainer>
          ) : <div style={{ padding: 24, textAlign: "center", color: "#5F6884" }}>Loading…</div>}
        </div>
        {/* Chart 2: 90/10 finance pie */}
        <div data-testid="chart-finance-pie" style={{ background: "rgba(0,0,0,0.3)", padding: 14, borderRadius: 10 }}>
          <div style={{ fontSize: 12, color: "#8B94B8", marginBottom: 8 }}>
            Financial Split · Total: {pie ? fmtEur(pie.total) : "—"}
          </div>
          {pie && pie.total > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pie.slices} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {pie.slices.map((s, i) => <Cell key={i} fill={s.fill} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0B1220", border: "1px solid #FFD700", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div style={{ padding: 24, textAlign: "center", color: "#5F6884" }}>Ещё нет платежей</div>}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════ 6. DEFLATION VISUALIZER (Step 9) ══════════════════ */
function DeflationVisualizer() {
  const [d, setD] = useState(null);
  const [displayBurn, setDisplayBurn] = useState(0);
  useEffect(() => {
    const load = () => api.get("/admin-panel/deflation").then(r => setD(r.data)).catch(() => {});
    load(); const iv = setInterval(load, 30000); return () => clearInterval(iv);
  }, []);
  // Live-increment burn counter (visual, based on current burn rate)
  useEffect(() => {
    if (!d) return;
    setDisplayBurn(d.cumulative_burn_tokens);
    // 0.5% per day = ~57.87 tokens/sec on 1B supply
    const perSec = (1_000_000_000 * d.daily_burn_pct) / 86400;
    const iv = setInterval(() => setDisplayBurn((v) => v + perSec), 1000);
    return () => clearInterval(iv);
  }, [d]);
  return (
    <section data-testid="admin-deflation" style={sectionStyle}>
      <div style={sectionHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Flame size={20} color="#FF3D6C" />
          <h2 style={{ margin: 0, fontSize: 18, color: "#FF3D6C" }}>Live Deflation Visualizer — 0.5% daily burn</h2>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        <MiniStat testId="burn-cumulative" label="Cumulative Burn (TGC)" value={fmtNum(Math.floor(displayBurn))} color="#FF3D6C" />
        <MiniStat testId="burn-circulating" label="Circulating Supply" value={fmtNum(Math.floor((d?.current_circulating || 1_000_000_000) - (displayBurn - (d?.cumulative_burn_tokens || 0))))} color="#10F4A7" />
        <MiniStat testId="burn-waitlist" label="Merchants Waitlist" value={fmtNum(d?.waitlist_rows)} color="#FFD700" />
        <MiniStat testId="burn-days" label="Days Since Launch" value={fmtNum(d?.days_since_launch)} color="#00F2FF" />
      </div>
    </section>
  );
}

/* ══════════════════ 7. NODE TREE VIEWER (Step 9) ══════════════════ */
function NodeTreeViewer() {
  const [uid, setUid] = useState("");
  const [tree, setTree] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = async () => {
    if (!uid.trim()) return;
    setBusy(true); setErr(""); setTree(null);
    try { const r = await api.get(`/admin-panel/tree/${uid.trim()}`); setTree(r.data); }
    catch (e) { setErr(e.response?.data?.detail || "Not found"); }
    finally { setBusy(false); }
  };
  return (
    <section data-testid="admin-node-tree" style={sectionStyle}>
      <div style={sectionHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <GitBranch size={20} color="#BC13FE" />
          <h2 style={{ margin: 0, fontSize: 18, color: "#BC13FE" }}>Node Tree Viewer — 8-Level Drill-Down</h2>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <input value={uid} onChange={(e) => setUid(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Provider user_id (UUID)"
          data-testid="tree-uid-input"
          style={{ flex: 1, padding: "10px 12px", borderRadius: 8, background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.1)", color: "#E7ECFF", fontSize: 13 }} />
        <button onClick={load} disabled={busy} data-testid="tree-load-btn"
          style={{ ...btnPrimary, background: "#BC13FE", color: "#fff" }}>{busy ? "…" : "Загрузить дерево"}</button>
      </div>
      {err && <div data-testid="tree-error" style={{ color: "#FF3D6C", fontSize: 12, marginBottom: 10 }}>⚠ {err}</div>}
      {tree && (
        <div data-testid="tree-content">
          <div style={{ fontSize: 12, color: "#8B94B8", marginBottom: 10 }}>
            Total nodes: <b style={{ color: "#BC13FE" }}>{tree.total_nodes}</b> · Levels: <b>{tree.levels_populated}</b>
          </div>
          <div style={{ maxHeight: 400, overflowY: "auto", padding: 12, background: "rgba(0,0,0,0.3)", borderRadius: 8, fontSize: 12, fontFamily: "monospace" }}>
            {flattenTree(tree.root).map((n) => (
              <div key={n.user_id} style={{ marginLeft: n.level * 14, borderLeft: n.level > 0 ? "1px dashed rgba(255,255,255,0.1)" : "none", paddingLeft: n.level > 0 ? 8 : 0, padding: "3px 0", display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ color: n.is_agent_active ? "#10F4A7" : "#8B94B8" }}>●</span>
                <code style={{ color: "#00F2FF" }}>{(n.wallet_address || n.user_id).slice(0, 14)}…</code>
                <span style={{ color: "#5F6884" }}>L{n.level}</span>
                {n.is_genesis_provider && <span style={{ color: "#FFD700" }}>★</span>}
                <span style={{ color: "#8B94B8" }}>· pts today: <b style={{ color: "#10F4A7" }}>{n.merchant_points_today || 0}</b></span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function flattenTree(root) {
  const out = [];
  const stack = [root];
  while (stack.length) {
    const n = stack.shift();
    out.push(n);
    if (n.children && n.children.length) {
      // Depth-first — push children to the front of the queue
      stack.unshift(...n.children);
    }
  }
  return out;
}

